import { useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { SEO } from './components/SEO';

// Components
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { LeadPopup } from './components/LeadPopup';

// Pages
import { Home } from './pages/Home';
import { About } from './pages/About';
import { Attorneys } from './pages/Attorneys';
import { Contact } from './pages/Contact';
import { ServicePage } from './pages/ServicePage';
import ThankYou from './pages/ThankYou';
import Thanks from './pages/Thanks';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

function App() {
  const [isLeadPopupOpen, setIsLeadPopupOpen] = useState(false);

  const openLeadPopup = () => setIsLeadPopupOpen(true);
  const closeLeadPopup = () => setIsLeadPopupOpen(false);

  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <ScrollToTop />
        <Header onOpenLeadPopup={openLeadPopup} />
        <main className="page-content">
          <Routes>
            <Route path="/" element={<Home onOpenLeadPopup={openLeadPopup} />} />
            <Route path="/about" element={<About onOpenLeadPopup={openLeadPopup} />} />
            <Route path="/attorneys" element={<Attorneys onOpenLeadPopup={openLeadPopup} />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/services/:slug" element={<ServicePage onOpenLeadPopup={openLeadPopup} />} />
            <Route path="/application/main" element={<ThankYou />} />
            <Route path="/thanks" element={<Thanks />} />
            {/* Legal pages - placeholder routes */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Home onOpenLeadPopup={openLeadPopup} />} />
          </Routes>
        </main>
        <Footer onOpenLeadPopup={openLeadPopup} />
        <LeadPopup isOpen={isLeadPopupOpen} onClose={closeLeadPopup} />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

// Simple legal pages
const PrivacyPage = () => (
  <div className="page-wrapper" data-testid="privacy-page">
    <SEO 
      title="Privacy Policy"
      description="Legal Emphasis Privacy Policy. Learn how we collect, use, and protect your personal information when you use our legal services."
      keywords="privacy policy, data protection, personal information, legal firm privacy"
    />
    <section className="bg-[#0A2A5E] py-20 md:py-32">
      <div className="le-container">
        <h1 className="le-heading-1 text-white">Privacy Policy</h1>
      </div>
    </section>
    <section className="le-section bg-white">
      <div className="le-container max-w-3xl">
        <div className="prose prose-lg">
          <p className="text-gray-600 leading-relaxed mb-6">
            Legal Emphasis ("we," "our," or "us") is committed to protecting the privacy and security of your personal information. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you visit our website, engage our legal services, or communicate with us.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Information We Collect</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            We collect several types of information to provide and improve our legal services:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li><strong>Personal Information:</strong> Name, email address, phone number, mailing address, and other contact details you provide when contacting us or engaging our services.</li>
            <li><strong>Case Information:</strong> Details about your legal matter, including documents, correspondence, and other information relevant to your case.</li>
            <li><strong>Website Usage Data:</strong> Information about how you interact with our website, including IP address, browser type, pages visited, and time spent on pages.</li>
            <li><strong>Communication Records:</strong> Records of your communications with us, including emails, phone calls, and meeting notes.</li>
          </ul>

          <h2 className="le-heading-3 mt-8 mb-4">How We Use Your Information</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>To provide legal services and respond to your inquiries</li>
            <li>To communicate with you about your case and our services</li>
            <li>To comply with legal and regulatory obligations</li>
            <li>To improve our website and service offerings</li>
            <li>To send you relevant updates and information (with your consent)</li>
            <li>To protect our rights and the rights of our clients</li>
          </ul>

          <h2 className="le-heading-3 mt-8 mb-4">Information Sharing and Disclosure</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>With your explicit consent</li>
            <li>To comply with legal obligations, court orders, or legal processes</li>
            <li>To protect our rights, privacy, safety, or property</li>
            <li>With service providers who assist in our operations (under strict confidentiality agreements)</li>
          </ul>

          <h2 className="le-heading-3 mt-8 mb-4">Attorney-Client Privilege</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Communications between you and Legal Emphasis regarding legal matters are protected by attorney-client privilege. We maintain strict confidentiality of all client communications and case-related information in accordance with our professional and ethical obligations.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Data Security</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes secure data storage, encrypted communications, and restricted access to confidential information.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Your Rights</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            You have certain rights regarding your personal information:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Right to access the personal information we hold about you</li>
            <li>Right to request correction of inaccurate information</li>
            <li>Right to request deletion of your information (subject to legal retention requirements)</li>
            <li>Right to opt out of marketing communications</li>
          </ul>

          <h2 className="le-heading-3 mt-8 mb-4">Cookies and Tracking</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can control cookie preferences through your browser settings.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Changes to This Policy</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Contact Us</h2>
          <p className="text-gray-600 leading-relaxed">
            If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at:<br /><br />
            <strong>Legal Emphasis</strong><br />
            One Sansome Street, Sansome St Ste 1400<br />
            San Francisco, CA 94104<br />
            Email: info@legalemphasis.com<br />
            Phone: +1 (888) 750-5204
          </p>
        </div>
      </div>
    </section>
  </div>
);

const TermsPage = () => (
  <div className="page-wrapper" data-testid="terms-page">
    <SEO 
      title="Terms of Service"
      description="Terms of Service for Legal Emphasis. Read our terms and conditions for using our website and engaging our legal services."
      keywords="terms of service, terms and conditions, legal terms, website terms"
    />
    <section className="bg-[#0A2A5E] py-20 md:py-32">
      <div className="le-container">
        <h1 className="le-heading-1 text-white">Terms of Service</h1>
      </div>
    </section>
    <section className="le-section bg-white">
      <div className="le-container max-w-3xl">
        <div className="prose prose-lg">
          <p className="text-gray-600 leading-relaxed mb-6">
            Welcome to the Legal Emphasis website. These Terms of Service ("Terms") govern your access to and use of our website and services. By accessing or using our website, you agree to be bound by these Terms.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Acceptance of Terms</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            By accessing this website, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, please do not use this website.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Our Services</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Legal Emphasis provides legal services including corporate law, intellectual property, immigration, business litigation, government contracting, and related legal counsel. This website provides information about our firm, our attorneys, and our practice areas to help you understand how we may assist with your legal needs.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Attorney-Client Relationship</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            An attorney-client relationship with Legal Emphasis is only established through a formal written engagement agreement signed by both parties. Merely visiting this website, sending us an inquiry, or communicating with us does not create an attorney-client relationship. Until such a relationship is established, any information you provide to us may not be treated as confidential.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Website Use</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            You agree to use this website only for lawful purposes and in accordance with these Terms. You agree not to:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Use this website in any way that violates any applicable law or regulation</li>
            <li>Attempt to gain unauthorized access to any portion of the website or any systems or networks connected to the website</li>
            <li>Use the website to transmit any harmful or malicious code</li>
            <li>Reproduce, duplicate, copy, or exploit any portion of the website without our express written permission</li>
          </ul>

          <h2 className="le-heading-3 mt-8 mb-4">Intellectual Property</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            All content on this website, including text, graphics, logos, images, and software, is the property of Legal Emphasis or its content suppliers and is protected by United States and international copyright laws. You may not reproduce, distribute, or create derivative works from any content on this website without our prior written consent.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Accuracy of Information</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            While we strive to keep the information on this website accurate and current, laws and regulations change frequently. We make no representations or warranties about the accuracy, completeness, or suitability of any information on this website. For specific legal advice, please contact our attorneys directly.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Third-Party Links</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            This website may contain links to third-party websites. These links are provided for your convenience only. We do not endorse or make any representations about these third-party websites, and we are not responsible for their content or privacy practices.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Limitation of Liability</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            To the fullest extent permitted by law, Legal Emphasis shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your access to or use of this website, or any information, content, or services obtained through this website.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Indemnification</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            You agree to indemnify and hold harmless Legal Emphasis, its partners, attorneys, employees, and agents from any claims, damages, losses, or expenses arising from your use of this website or your violation of these Terms.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Governing Law</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any legal action arising from these Terms shall be brought in the state or federal courts located in San Francisco County, California.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Modifications</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            We reserve the right to modify these Terms at any time. Any changes will be effective immediately upon posting on this website. Your continued use of the website after any changes indicates your acceptance of the modified Terms.
          </p>

          <h2 className="le-heading-3 mt-8 mb-4">Contact Information</h2>
          <p className="text-gray-600 leading-relaxed">
            If you have any questions about these Terms, please contact us at:<br /><br />
            <strong>Legal Emphasis</strong><br />
            One Sansome Street, Sansome St Ste 1400<br />
            San Francisco, CA 94104<br />
            Email: info@legalemphasis.com<br />
            Phone: +1 (888) 750-5204
          </p>
        </div>
      </div>
    </section>
  </div>
);

export default App;
